import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { UNIT_PRESETS, fromUnits, limpiarCodigo, parseMoney, type Centavos } from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { crearProducto, etiquetaListaPrecio, lecturaDeCodigo, ordenarListas } from '../lib/catalogo.js';
import { usePuede } from '../lib/sesion.js';
import {
  categoriaSugerida,
  fotoAbierta,
  nombreSugerido,
  useCodigoAbierto,
  type DatosAbiertos,
  type EstadoConsulta,
} from '../lib/catalogos-abiertos.js';
import { fotoDeArchivo } from '../lib/fotos.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo, Interruptor, Selector } from './Campo.js';
import { CampoCodigo } from './CampoCodigo.js';
import { ElegirFoto } from './ElegirFoto.js';
import { Chip, Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';
import { Modal } from './Modal.js';
import { CampoCategoria } from './CampoCategoria.js';
import { SugerenciaAbierta } from './SugerenciaAbierta.js';

/**
 * Base units a product's stock can be held in. Containers are sale units, defined per product.
 * «Editar producto» offers the same ones.
 */
export const BASES = UNIT_PRESETS.filter((u) => u.code === u.base);

/**
 * A new product, from its name to its prices: the form of «Nuevo producto», and of the sheet that
 * makes one in the middle of receiving goods.
 *
 * It keeps what is typed in it itself, so that over a purchase a key re-draws the form and not
 * the purchase behind the sheet, and the screen that opened it hears once, when it is saved.
 *
 * Opened with a code the open catalogues know (`lib/catalogos-abiertos.ts`), it fills itself in
 * from what they say: the name with its brand and size, the English one, the shop's category for
 * it and the photo of the front. Only what nobody has touched yet, and saying where it came from.
 *
 * On its own screen (`lectorUsb`) it also works the way receiving goods does (the owner,
 * 2026-10-02). The barcode field comes first. A USB scanner's code goes into it wherever the
 * cursor was, and the field the cursor was in is left as it was. A code scanned, from the camera
 * or typed and entered is answered in a sheet, whatever it is (`CodigoLeido`): whose it is, when
 * the shop has it; «¿Es este producto?», when a catalogue knows it; and otherwise that it is new.
 * «Usar estos datos» fills the form in; one of the shop's own lookalikes takes the code instead,
 * and the screen goes to it (`onYaExiste`), so that no duplicate is made of something the shop
 * already sells. Once the sheet is answered, the cursor goes back where it was.
 */
export function FormularioProducto({
  codigo: codigoInicial = '',
  nombre: nombreInicial = '',
  enHoja = false,
  lectorUsb = false,
  onYaExiste,
  onCreado,
}: {
  /** A code nobody had, scanned or typed: already in its field. */
  codigo?: string;
  /** The name it was looked for, or sold, under. */
  nombre?: string;
  /** Inside a sheet, which is card enough: its parts are not cards of their own. */
  enHoja?: boolean;
  /** A USB scanner's code is the barcode field's, wherever the cursor is. Not in a sheet, whose screen reads its own. */
  lectorUsb?: boolean;
  /** One of the shop's products, suggested for the code scanned, which has just taken it. */
  onYaExiste?: (p: Producto) => void;
  onCreado: (creado: { productoId: string; unidadVentaId: string }) => void;
}) {
  const t = useT();
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const activas = ordenarListas(listas);
  /** A new product's prices are prices too: the owner's (2026-10-13). A cashier makes it without them. */
  const puedePrecios = usePuede('editar_precios');

  const [nombre, setNombre] = useState(nombreInicial);
  const [nombreEn, setNombreEn] = useState('');
  const [foto, setFoto] = useState<string | null>(null);
  const [categoria, setCategoria] = useState('Abarrotes');
  const [unidadBase, setUnidadBase] = useState('unidad');
  const [esMedicamento, setEsMedicamento] = useState(false);
  const [perecedero, setPerecedero] = useState(false);
  const [registroSanitario, setRegistro] = useState('');
  const [codigo, setCodigo] = useState(codigoInicial);
  const [preciosTexto, setPreciosTexto] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** The fields somebody changed: what the catalogues say does not write over them unasked. */
  const tocados = useRef(new Set<string>());
  const tocar = (campo: string) => tocados.current.add(campo);
  /** Photos chosen by hand, counted: one chosen after the catalogue's was asked for wins over it. */
  const fotosElegidas = useRef(0);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);
  /** The catalogue the form was last filled in from, for the line that says so. */
  const [llenadoDe, setLlenadoDe] = useState<string | null>(null);

  /**
   * What a catalogue says, into the form: only into what nobody has touched, or over everything
   * when somebody asked for it («Usar estos datos»). The photo is made the product's own, as one
   * taken with the camera is: square, small, kept on the device.
   */
  function llenarCon(d: DatosAbiertos, encima: boolean) {
    const libre = (campo: string) => encima || !tocados.current.has(campo);
    const sugerido = nombreSugerido(d);
    if (libre('nombre') && sugerido) setNombre((n) => (encima ? sugerido : n.trim() || sugerido));
    // Brand and size around the English one too: a catalogue's English name is often just «Coffee».
    const ingles = d.nombreEn ? nombreSugerido({ ...d, nombre: d.nombreEn }) : '';
    if (libre('nombreEn') && ingles) setNombreEn((n) => (encima ? ingles : n || ingles));
    if (libre('categoria')) setCategoria(categoriaSugerida(d));
    setLlenadoDe(d.fuente ?? '');
    if (!d.foto || !libre('foto')) return;
    const elegidas = fotosElegidas.current;
    void fotoAbierta(d.codigo)
      .then((blob) => (blob ? fotoDeArchivo(blob) : null))
      .then((nombreFoto) => {
        if (!nombreFoto || !vivo.current || fotosElegidas.current !== elegidas) return;
        setFoto((f) => (encima ? nombreFoto : (f ?? nombreFoto)));
      })
      .catch(() => undefined);
  }

  // Opened with a code: filled in from what the catalogues say, by itself.
  const consulta = useCodigoAbierto(codigoInicial || null);
  const deCatalogo = consulta.estado === 'listo' && consulta.datos?.encontrado ? consulta.datos : null;
  useEffect(() => {
    // Once per answer: the function is new every render, the answer is not.
    if (deCatalogo) llenarCon(deCatalogo, false);
  }, [deCatalogo]);

  // Scanned here: what is known about it, in a sheet, whatever it is.
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const campoCodigo = useRef<HTMLInputElement>(null);
  /** The code scanned or entered: its sheet until it is answered, and what a catalogue said after. */
  const [escaneado, setEscaneado] = useState<string | null>(null);
  /** The scanned code whose sheet was answered, either way. */
  const [respondido, setRespondido] = useState<string | null>(null);
  /** Where the cursor was when the sheet opened, for it to go back to: a name half typed, say. */
  const cursorAntes = useRef<HTMLElement | null>(null);
  /** The shop's own products with it already: its sheet says whose, with a way to them, and nothing is looked up. */
  const deOtros = escaneado
    ? [
        ...new Map(
          lecturaDeCodigo(productos, unidades, escaneado, { inactivos: true }).map(({ producto }) => [producto.id, producto]),
        ).values(),
      ]
    : [];
  const deOtro = deOtros[0] ?? null;
  /**
   * The code somebody said is on a different product too, one code a factory printed on two: the
   * field then says who else carries it, calmly, instead of warning that it is somebody's already.
   */
  const [otroConElMismo, setOtroConElMismo] = useState<string | null>(null);
  const deEscaneo = useCodigoAbierto(escaneado && !deOtro ? escaneado : null);
  const sugerencia = deEscaneo.estado === 'listo' && deEscaneo.datos?.encontrado ? deEscaneo.datos : null;

  function recordarCursor() {
    cursorAntes.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  function leido(leidoAhora: string) {
    const limpio = limpiarCodigo(leidoAhora);
    if (!limpio) return;
    setCodigo(limpio);
    recordarCursor();
    setEscaneado(limpio);
    setRespondido(null);
  }
  useLectorUsb(campoCodigo, leido, { activo: lectorUsb && !enHoja, enrutar: false });

  /** The sheet answered, whichever way: the cursor goes back where it was before it opened. */
  function responder() {
    setRespondido(escaneado);
    const antes = cursorAntes.current;
    cursorAntes.current = null;
    if (antes?.isConnected) antes.focus();
  }

  /**
   * The barcode field, first on the product's own screen: what is in the hand is where making a
   * product starts there (the owner, 2026-10-02), as the scanner is first in «Mercadería comprada».
   * The cursor still starts in the name, since a scan reaches this field from wherever it is and the
   * name is what is typed next. In the purchase's sheet the code came along, and it stays below.
   */
  const codigoPrimero = lectorUsb && !enHoja;
  const campoDeCodigo = (
    <CampoCodigo
      ref={campoCodigo}
      valor={codigo}
      compartido={otroConElMismo !== null && codigo === otroConElMismo}
      onCambio={(c) => {
        setCodigo(c);
        if (c !== escaneado) setEscaneado(null);
      }}
      {...(codigoPrimero
        ? {
            // Typed by hand and entered, it is asked about as a scan would be.
            onEnter: () => leido(codigo),
            onEscaneado: leido,
          }
        : {})}
      ayuda={
        <EstadoDelCodigo
          consulta={deEscaneo}
          onVer={() => {
            recordarCursor();
            setRespondido(null);
          }}
        />
      }
    />
  );

  const listo = nombre.trim().length > 0;
  const parte = enHoja ? 'mt-6' : 'tarjeta mt-4';
  /** Under the sheet's own title, a level down from where it is under the screen's. */
  const Subtitulo = enHoja ? 'h3' : 'h2';

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const precios = puedePrecios
        ? activas
            .map((l) => ({ listaId: l.id, precio: parseMoney(preciosTexto[l.id] ?? '') }))
            .filter((p): p is { listaId: string; precio: Centavos } => p.precio !== null && p.precio > 0)
        : [];

      onCreado(
        await crearProducto({
          nombre,
          ...(nombreEn.trim() ? { nombreEn } : {}),
          ...(foto ? { foto } : {}),
          categoria,
          unidadBase,
          esMedicamento,
          perecedero,
          ...(registroSanitario.trim() ? { registroSanitario: registroSanitario.trim() } : {}),
          codigosBarras: [codigo],
          unidadVentaNombre: unidadBase,
          factor: fromUnits(1),
          precios,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
      setGuardando(false);
    }
  }

  return (
    <>
      <div className={`${enHoja ? '' : 'tarjeta'} flex flex-col gap-4`}>
        {codigoPrimero && campoDeCodigo}
        {/*
          The drawing beside the button follows what is typed below it, so the square is never
          empty — and a product saved without a photo already looks the way it will in a list.
        */}
        {consulta.estado === 'buscando' && (
          <p role="status" className="flex items-center gap-2 text-sm text-slate-400">
            <Ico icono="buscar" className="size-4 shrink-0 animate-pulse" />
            {t('abiertos.buscando')}
          </p>
        )}
        {llenadoDe !== null && (
          <p className="flex items-start gap-2 rounded-xl bg-teal-500/10 px-3 py-2 text-sm text-teal-200" data-llenado>
            <Ico icono="buscar" className="mt-0.5 size-4 shrink-0" />
            {t('abiertos.llenado', { fuente: llenadoDe })}
          </p>
        )}
        <ElegirFoto
          producto={{ nombre, nombreEn, categoria, foto }}
          onElegida={(f) => {
            tocar('foto');
            fotosElegidas.current += 1;
            setFoto(f);
          }}
          onQuitada={() => {
            tocar('foto');
            fotosElegidas.current += 1;
            setFoto(null);
          }}
        />

        <div data-guia="producto-nombre">
          <Campo
            maxLength={120}
            etiqueta={t('producto.nombre')}
            value={nombre}
            onChange={(e) => {
              tocar('nombre');
              setNombre(e.target.value);
            }}
            autoFocus
            autoComplete="off"
            placeholder={t('carga.ejemploNombre')}
          />
        </div>

        <Campo
          maxLength={120}
          etiqueta={t('producto.nombreEn')}
          value={nombreEn}
          onChange={(e) => {
            tocar('nombreEn');
            setNombreEn(e.target.value);
          }}
          autoComplete="off"
          lang="en"
          ayuda={t('producto.ayudaNombreEn')}
        />

        <div data-guia="producto-categoria">
          <CampoCategoria
            value={categoria}
            onChange={(c) => {
              tocar('categoria');
              setCategoria(c);
            }}
          />
        </div>

        <div data-guia="producto-unidad">
          <Selector
            etiqueta={t('producto.unidadBase')}
            value={unidadBase}
            onChange={(e) => setUnidadBase(e.target.value)}
            ayuda={t('producto.ayudaUnidad')}
          >
            {BASES.map((u) => (
              <option key={u.code} value={u.code}>
                {etiquetaUnidad(u.code, u.label)}
              </option>
            ))}
          </Selector>
        </div>

        {!codigoPrimero && campoDeCodigo}
      </div>

      {!puedePrecios && activas.length > 0 && (
        <p className={`${enHoja ? 'mt-6' : 'mt-4'} text-sm text-slate-400`} data-producto-precios-del-dueno>
          {t('producto.preciosDelDueno')}
        </p>
      )}
      {puedePrecios && activas.length > 0 && (
        <div className={`${parte} flex flex-col gap-4`}>
          <Subtitulo className="text-sm font-semibold text-slate-400">{t('producto.precios')}</Subtitulo>
          {activas.map((l) => (
            <Campo
              key={l.id}
              etiqueta={etiquetaListaPrecio(l)}
              inputMode="decimal"
              className="tabular"
              placeholder="0.00"
              value={preciosTexto[l.id] ?? ''}
              onChange={(e) => setPreciosTexto((p) => ({ ...p, [l.id]: e.target.value }))}
            />
          ))}
        </div>
      )}

      <div className={parte}>
        <Interruptor
          etiqueta={t('producto.esMedicamento')}
          descripcion={t('producto.descMedicamento')}
          checked={esMedicamento}
          onChange={setEsMedicamento}
        />
        <Interruptor
          etiqueta={t('producto.esPerecedero')}
          descripcion={t('producto.descPerecedero')}
          checked={perecedero}
          onChange={setPerecedero}
        />
        {esMedicamento && (
          <div className="mt-3">
            <Campo
              maxLength={60}
              etiqueta={t('producto.registroSanitario')}
              value={registroSanitario}
              onChange={(e) => setRegistro(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
      </div>

      {error && <p className="mt-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}

      <button
        type="button"
        className="btn-primario mt-5 w-full"
        data-guia="producto-guardar"
        disabled={!listo || guardando}
        onClick={() => void guardar()}
      >
        <Ico icono="guardar" />
        {t(guardando ? 'comun.guardando' : 'producto.guardar')}
      </button>

      {escaneado && respondido !== escaneado && (
        <Modal
          titulo={t(deOtro ? 'codigo.yaEstaTitulo' : sugerencia ? 'abiertos.esEste' : 'codigo.nuevoTitulo')}
          onCerrar={responder}
        >
          <CodigoLeido
            codigo={escaneado}
            deOtros={deOtros}
            consulta={deEscaneo}
            onUsar={() => {
              if (sugerencia) llenarCon(sugerencia, true);
              responder();
            }}
            onSeguir={responder}
            onOtro={() => {
              setOtroConElMismo(escaneado);
              responder();
            }}
            onElegido={(p) => {
              setRespondido(escaneado);
              onYaExiste?.(p);
            }}
          />
        </Modal>
      )}
    </>
  );
}

/**
 * What is known about the code just scanned, in a sheet whatever it is. A line under the field used
 * to say it in all but one case, which was easy to miss (the owner, 2026-10-02).
 *   · The shop has it already: whose it is, and two plain answers. «Es el mismo producto» opens
 *     that one, since the goods are most likely in the catalogue and this would be a second copy
 *     of them. «Es otro producto con el mismo código» goes on making this one: some factories print
 *     one code on two products (the owner, 2026-10-05), and scanning it then asks which is in the
 *     hand (`CualEs`). It said «Seguir aquí», which did the same without saying so.
 *   · A catalogue knows it: «¿Es este producto?», what it says and the shop's own lookalikes, with
 *     «Usar estos datos».
 *   · Otherwise it is new, and the sheet says what the catalogues said, that they are being asked,
 *     or why they were not.
 * As in the purchase's sheet, the cursor comes into it, out of the field behind: a name being typed
 * when it opens presses none of its buttons, nor does a scanner's Enter.
 */
function CodigoLeido({
  codigo,
  deOtros,
  consulta,
  onUsar,
  onSeguir,
  onOtro,
  onElegido,
}: {
  codigo: string;
  /** The shop's own products with this code: usually none, or one. */
  deOtros: readonly Producto[];
  consulta: EstadoConsulta;
  onUsar: () => void;
  /** The form, then, as it was. */
  onSeguir: () => void;
  /** Another product with the same code: the form as it was, the code staying in it. */
  onOtro: () => void;
  onElegido: (p: Producto) => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => raiz.current?.focus(), []);
  const encontrado = consulta.estado === 'listo' && Boolean(consulta.datos?.encontrado);
  const deOtro = deOtros[0];
  return (
    <div ref={raiz} tabIndex={-1} className="flex flex-col gap-4 outline-none" data-pregunta-datos>
      {deOtro && deOtros.length === 1 ? (
        <>
          <div className="flex items-center gap-3">
            <Miniatura producto={deOtro} tamano="md" />
            <p className="min-w-0 font-medium break-words">
              {t(deOtro.activo ? 'codigo.yaEsDe' : 'codigo.yaEsDeDesactivado', { producto: nombre(deOtro) })}
            </p>
          </div>
          <p className="text-sm text-slate-400">{t('codigo.mismoUOtro')}</p>
          <Link to="/productos/$productoId" params={{ productoId: deOtro.id }} className="btn-primario">
            <Ico icono="adelante" />
            {t('codigo.esElMismo')}
          </Link>
        </>
      ) : deOtros.length > 1 ? (
        <>
          {/* Already on two: each is a way to itself, as the one answer is when there is one. */}
          <p className="font-medium">{t('codigo.yaLoLlevan', { n: deOtros.length })}</p>
          <ul className="flex flex-col gap-2">
            {deOtros.map((p) => (
              <li key={p.id}>
                <Link
                  to="/productos/$productoId"
                  params={{ productoId: p.id }}
                  className="tarjeta-accion flex w-full items-center gap-3 p-3 text-left"
                  data-sin-icono="fila"
                >
                  <Miniatura producto={p} tamano="md" />
                  <span className="min-w-0 flex-1 font-medium break-words">{nombre(p)}</span>
                  <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-400">{t('codigo.esUnoDeEstos')}</p>
        </>
      ) : (
        <>
          {!encontrado && (
            <div className="flex items-start gap-3">
              <Chip icono="nuevo" tinte="marca" />
              <p className="min-w-0 self-center font-medium break-words">{t('buscador.codigoDesconocido', { codigo })}</p>
            </div>
          )}
          <SugerenciaAbierta codigo={codigo} conPista={false} onElegido={onElegido} />
          {encontrado && (
            <button type="button" className="btn-primario" onClick={onUsar}>
              <Ico icono="listo" />
              {t('abiertos.usar')}
            </button>
          )}
        </>
      )}
      {deOtro ? (
        <button type="button" className="btn-secundario" onClick={onOtro}>
          <Ico icono="nuevo" />
          {t('codigo.esOtro')}
        </button>
      ) : (
        <button type="button" className={encontrado ? 'btn-secundario' : 'btn-primario'} onClick={onSeguir}>
          <Ico icono={encontrado ? 'cerrar' : 'editar'} />
          {t(encontrado ? 'abiertos.noUsar' : 'codigo.loLlenoYo')}
        </button>
      )}
    </div>
  );
}

/**
 * Under the barcode field, once a code's sheet is answered: a way back to what a catalogue said
 * about it. The field's usual help otherwise: what is known about a code scanned is said in its
 * sheet (`CodigoLeido`).
 */
function EstadoDelCodigo({ consulta, onVer }: { consulta: EstadoConsulta; onVer: () => void }) {
  const t = useT();
  const d = consulta.estado === 'listo' ? consulta.datos : null;
  if (!d?.encontrado) return t('producto.ayudaCodigo');
  return (
    <button type="button" className="btn-texto gap-1 text-xs text-teal-300" onClick={onVer}>
      <Ico icono="buscar" className="size-3.5" />
      {t('abiertos.ver', { fuente: d.fuente ?? '' })}
    </button>
  );
}
